import { Component, OnDestroy, OnInit } from '@angular/core';
import * as d3 from 'd3';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { ICONS } from 'src/app/core/constants/icons';
import { MESSAGES } from 'src/app/core/constants/messages';
import { UnitConstants } from 'src/app/core/constants/unit';
import { CALC_TYPES } from 'src/app/core/enums/calc-types';
import { Catenary, CatenaryAnswer } from 'src/app/core/models/catenary-answer';
import { Line } from 'src/app/core/models/line.model';
import { MeshInstructions } from 'src/app/core/models/mesh-instructions';
import { ResultInterval } from 'src/app/core/models/results/result-interval.model';
import { Simulation } from 'src/app/core/models/simulation.model';
import { CatenaryService } from 'src/app/core/services/catenary/catenary.service';
import { DataSharingService } from 'src/app/core/services/data-sharing/data-sharing.service';
import { EnvironmentPropsService } from 'src/app/core/services/environment-props/environment-props.service';
import { MapsService } from 'src/app/core/services/simulation/objects/maps/maps.service';
import { SimulationManagerService } from 'src/app/core/services/simulation/simulation-manager.service';
import { NumberToSciNotationPipe } from '../../pipes/number-to-sci-notation/number-to-sci-notation.pipe';
import { CatenaryInfo } from './catenary-info.model';
import { Heatmap } from './methods/catenary-heatmap/catenary-heatmap.method';

interface TooltipData {
  segIndex: number;
  segLength: number;
  catenary: { x: number; y: number }[];
  catenaryIndex: number;
  sliceOrganizer?: any[];
  regionSliceOrganizer?: any[];
}

@Component({
  selector: 'app-catenary-generator',
  templateUrl: './catenary-generator.component.html',
  styleUrls: ['./catenary-generator.component.scss'],
  providers: [NumberToSciNotationPipe],
})
export class CatenaryGeneratorComponent implements OnInit, OnDestroy {
  pageTitle: string;
  errorTitle: string;
  errorMessages: any;

  catenaryCreated = false;
  failedCatenaries: number[];
  catenaries: CatenaryAnswer = null;

  width = 1000;
  height = 1000;
  margin = { top: 50, right: 20, bottom: 80, left: 100 };
  divisionScalingDenominator = 100;
  divisionCircleRadius = 0;
  tooltipScallingDenominator = 30;
  maxZoom = 6;
  lineThickness = 5;
  flexjointStroke = 13;
  tooltipFontSize: number;
  triangleSideSize: number;

  svg: any = null;
  chart: any = null;
  yScale: any = null;
  fullXScale: any = null;
  xGrid: any = null;
  yGrid: any = null;
  heatmap: any = null;
  interpolateColor: any = null;

  yLabel = `Y (${UnitConstants.DEFAULT.LENGTH.value})`;
  xLabel = `X (${UnitConstants.DEFAULT.LENGTH.value})`;

  minXCatenary = 0;
  maxXCatenary = 0;
  minYCatenary = 0;
  maxYCatenary = 0;
  fullMaxX = 0;

  numXTicks = 10;
  numYTicks = 5;

  widthToUse = 0;
  heightToUse = 0;

  simulationProblem = false;
  noConnections = false;
  noMaterial = false;
  noLine = false;
  zeroProjection = false;

  allCatenariesNoConverged = false;
  catenaryNoConverged = false;
  serverFailed = false;
  serverErrors = [];
  convertedSimulation: any;

  noConvergenceIcon = ICONS.LINE_COMPOSITION;
  useDesktopRepresentation = true;

  showFloaters = true;
  showStiffeners = true;
  showStrakes = true;
  showStressJoint = true;
  showFlexJoint = true;
  selectedLineTitle: string = null;
  selectedLineId: number = null;
  hasDefinedMeshHeatmap: boolean;
  showAccessoryButton: boolean = true;
  lineRegions: ResultInterval[];
  regionColor = 'rgba(0,0,0,0.4)';
  meshGradientSub: Subscription;
  lineRegionsSub: Subscription;
  subscription = new Subscription();

  meshHeatMapData = {
    totalPathLength: 0,
    minLength: 0,
    maxLength: 0,
    elementsLength: [],
    linesData: [],
    segmentLengths: [],
  };

  constructor(
    private simulationManager: SimulationManagerService,
    private catenaryService: CatenaryService,
    private dataSharingService: DataSharingService,
    private mapsService: MapsService,
    private environmentPropsService: EnvironmentPropsService,
  ) {
    this.pageTitle = CONSTANTS.TITLES.CATENARY_VIEW;
    this.errorTitle = CONSTANTS.TITLES.CATENARY_ERROR;
    this.errorMessages = MESSAGES.ERROR.CATENARY;

    this.catenaryCreated = false;
    this.tooltipFontSize = this.height / (this.tooltipScallingDenominator * 2);
    this.triangleSideSize = this.height / (3 * this.tooltipScallingDenominator);

    this.heatmap = new Heatmap();
    this.interpolateColor = d3.interpolateRgb(
      CONSTANTS.MESH_HEATMAP_COLORS.LOW_INTENSITY,
      CONSTANTS.MESH_HEATMAP_COLORS.HIGH_INTENSITY,
    );
  }

  ngOnInit(): void {
    this.watchLinePropsState();
    this.watchMeshGradientState();
    this.watchRegionsState();
    this.watchCatenaryState();
  }

  ngOnDestroy(): void {
    this.svg?.selectAll('*').remove();
    this.serverFailed = false;
    this.serverErrors = [];
    this.subscription?.unsubscribe();
    this.meshGradientSub?.unsubscribe();
    this.lineRegionsSub?.unsubscribe();
  }

  watchCatenaryState(): void {
    const catenarySubscription = this.catenaryService.catenary$.subscribe((catenary) => {
      if (!catenary) {
        this.allCatenariesNoConverged = true;
        return;
      }

      this.generateCatenary(catenary);
    });
    this.subscription.add(catenarySubscription);
  }

  watchLinePropsState(): void {
    const selectedLineTitleSubscription = this.dataSharingService.selectedLineTitle$
      .pipe(debounceTime(300))
      .subscribe((title) => {
        this.selectedLineTitle = title;
      });
    const selectedLineIdSubscription = this.dataSharingService.selectedLineId$
      .pipe(debounceTime(300))
      .subscribe((lineId) => {
        this.selectedLineId = lineId;
      });
    this.subscription.add(selectedLineTitleSubscription);
    this.subscription.add(selectedLineIdSubscription);
  }

  watchMeshGradientState(): void {
    if (!this.isAnflex) {
      return;
    }

    this.meshGradientSub = this.dataSharingService.meshGradient$
      .pipe(debounceTime(CONSTANTS.CATENARY_DEBOUNCE))
      .subscribe((mesh: MeshInstructions) => {
        this.hasDefinedMeshHeatmap = !!mesh;
      });
    this.subscription.add(this.meshGradientSub);
  }

  watchRegionsState(): void {
    this.lineRegionsSub = this.dataSharingService.lineRegions$
      .pipe(debounceTime(CONSTANTS.CATENARY_DEBOUNCE))
      .subscribe((regions: any[]) => {
        this.lineRegions = regions;
      });
    this.subscription.add(this.lineRegionsSub);
  }

  setBounds(catenaryInfo: CatenaryInfo): boolean {
    this.maxXCatenary = Math.max(...catenaryInfo.allX);
    this.minXCatenary = Math.min(...catenaryInfo.allX);

    this.maxYCatenary = Math.max(...catenaryInfo.allY);
    this.minYCatenary = Math.min(...catenaryInfo.allY);

    const xOffset = this.maxXCatenary - this.minXCatenary;
    const yOffset = this.maxYCatenary - this.minYCatenary;

    if (yOffset === 0) {
      return false;
    }

    this.width = this.height * (xOffset / yOffset);

    this.widthToUse = this.width - this.margin.left - this.margin.right;
    this.heightToUse = this.height - this.margin.top - this.margin.bottom;

    const possibleCircleRadiusX = this.widthToUse / (2 * this.divisionScalingDenominator);
    const possibleCircleRadiusY = this.heightToUse / (2 * this.divisionScalingDenominator);
    this.divisionCircleRadius = Math.min(possibleCircleRadiusX, possibleCircleRadiusY);

    const fullWidth = Math.max(this.width, this.height);
    this.fullMaxX =
      this.minXCatenary + ((this.maxXCatenary - this.minXCatenary) * fullWidth) / this.width;

    this.svg = d3
      .select(CONSTANTS.SVG)
      .attr(CONSTANTS.VIEW_BOX, '0 0 ' + fullWidth + ' ' + this.height)
      .attr(CONSTANTS.WIDTH, '100%')
      .attr(CONSTANTS.PRESERVE_ASPECT_RATIO, CONSTANTS.SVG_MIN);

    this.svg.selectAll('*').remove();

    this.chart = this.svg.append('g');

    this.yScale = d3
      .scaleLinear()
      .range([this.height - this.margin.bottom, this.margin.top])
      .domain([this.minYCatenary, this.maxYCatenary]);
    this.fullXScale = d3
      .scaleLinear()
      .range([this.margin.left, fullWidth - this.margin.right])
      .domain([this.minYCatenary, this.fullMaxX]);

    this.setZoomProps();

    return true;
  }

  setZoomProps(): void {
    const chartTotalTranslateExtent = 300;
    const chartHalfTranslateExtent = chartTotalTranslateExtent / 2;
    const zoom = d3
      .zoom()
      .scaleExtent([1, this.maxZoom])
      .translateExtent([
        [-chartHalfTranslateExtent, -chartHalfTranslateExtent],
        [this.width + chartTotalTranslateExtent, this.height + chartHalfTranslateExtent],
      ]);

    this.svg.call(
      zoom.on('zoom', (e: any) => {
        this.chart.attr(CONSTANTS.TRANSFORM, e.transform);
      }),
    );
  }

  setAxisAndGrids(): void {
    const fullInnerWidth = Math.max(this.width, this.height) - this.margin.left - this.margin.right;
    const innerHeight = this.height - this.margin.top - this.margin.bottom;
    this.xGrid = d3
      .axisBottom(this.fullXScale)
      .tickSize(-innerHeight)
      .tickFormat(null)
      .ticks(this.numXTicks);
    this.yGrid = d3
      .axisLeft(this.yScale)
      .tickSize(-fullInnerWidth)
      .tickFormat(null)
      .ticks(this.numYTicks);

    this.chart
      .insert('g')
      .attr(CONSTANTS.TRANSFORM, 'translate(0,' + (this.height - this.margin.bottom) + ')')
      .call(this.xGrid);

    this.chart
      .insert('g')
      .attr(CONSTANTS.TRANSFORM, 'translate(' + this.margin.left + ',0)')
      .attr('class', 'axis axis--y')
      .call(this.yGrid);

    this.chart
      .insert('line')
      .attr('class', 'axis-x')
      .attr(CONSTANTS.X1, this.fullXScale(this.minXCatenary))
      .attr(CONSTANTS.Y1, this.yScale(this.minYCatenary))
      .attr(CONSTANTS.X2, this.fullXScale(this.fullMaxX))
      .attr(CONSTANTS.Y2, this.yScale(this.minYCatenary));

    this.drawAxisTitles(fullInnerWidth, innerHeight);
  }

  drawAxisTitles(fullInnerWidth, innerHeight): void {
    const legendGroup = this.chart.insert('g').attr('class', 'axis-titles');
    const x = (fullInnerWidth + this.margin.left) / 2;
    legendGroup
      .insert('text')
      .attr('class', 'axis-title')
      .attr('x', x)
      .attr('y', this.height - this.margin.top / 2)
      .attr(CONSTANTS.TRANSFORM, `translate(-50%,0)`)
      .style('alignment-baseline', 'middle')
      .text(this.xLabel);

    const titleX = this.margin.left / 3;
    const titleY = innerHeight / 2;

    legendGroup
      .insert('text')
      .attr('class', 'axis-title')
      .attr('x', -50)
      .attr('y', titleY)
      .style('alignment-baseline', 'middle')
      .attr('transform', `rotate(-90, ${titleX}, ${titleY})`)
      .text(this.yLabel);
  }

  generateCatenary(catenaries: CatenaryAnswer): void {
    if (!this.checkCatenaryRequirements()) {
      return;
    }

    if (catenaries?.lines) {
      this.serverFailed = false;
      this.serverErrors = [];
      this.catenaryCreated = true;
      this.catenaries = catenaries;
      this.generateGraphic();
      return;
    }

    this.setServerErrors(catenaries);
  }

  setServerErrors(catenaries: CatenaryAnswer): void {
    this.serverFailed = true;
    this.serverErrors = [];
    const validationErrors = catenaries?.validationErrors ?? [];
    if (!validationErrors) {
      return;
    }
    for (const errObj of validationErrors) {
      this.serverErrors.push(errObj.errorDesc);
    }
  }

  getCatenaryCreated(): boolean {
    return this.catenaryCreated;
  }

  filterList(initialList, includes): any[] {
    return initialList.filter((_, i) => includes[i]);
  }

  computeFlexJointDistances(segLengths, hasFlexJoint) {
    const fullLength = segLengths.reduce((a, b) => a + b);
    const distances = [];
    for (let idx = segLengths.length - 1; idx >= 0; idx--) {
      if (hasFlexJoint[idx]) {
        const newDist = segLengths.slice(idx, segLengths.length).reduce((a, b) => a + b);
        distances.push(fullLength - newDist);
      }
    }
    distances.reverse();
    return distances;
  }

  isSelectedCatenary(catenary: Catenary): boolean {
    if (!this.selectedLineTitle) {
      return true;
    }
    const { lineTitle } = catenary || { lineTitle: '' };
    return (
      lineTitle === this.selectedLineTitle ||
      lineTitle === `${this.selectedLineTitle}${CONSTANTS.MINIMUM_AFFIX}` ||
      lineTitle === `${this.selectedLineTitle}${CONSTANTS.MAXIMUM_AFFIX}` ||
      lineTitle === `${this.selectedLineTitle}${CONSTANTS.MOST_PROBABLE_AFFIX}`
    );
  }

  resetNoConvergencyFlags(): void {
    this.failedCatenaries = [];
    this.allCatenariesNoConverged = false;
    this.catenaryNoConverged = false;
  }

  computeCatenaries(): any {
    this.resetNoConvergencyFlags();

    const cat: { x: number; y: number }[][] = [];
    const catenaryLengths: number[][] = [];
    const hasFloater: boolean[][] = [];
    const hasStiffener: boolean[][] = [];
    const hasStrake: boolean[][] = [];
    const hasStressJoint: boolean[][] = [];
    const allX: number[] = [];
    const allY: number[] = [];
    const flexJointDistances: number[][] = [];

    this.catenaries?.lines.forEach((catenary) => {
      const selectedCatenary = this.isSelectedCatenary(catenary);
      if (!catenary.converged && selectedCatenary) {
        this.failedCatenaries.push(catenary.lineInternalId);
        return;
      }
      if (!selectedCatenary) {
        return;
      }

      const line: { x: number; y: number }[] = catenary.meshX.map((meshX, i) => {
        const x = -meshX;
        const y = catenary.meshY[i];
        allX.push(x);
        allY.push(y);
        return { x, y };
      });

      const withoutFlexJoint = catenary.segHasFlexJoint.map((x) => !x);
      const newDistances = this.computeFlexJointDistances(
        catenary.segLength,
        catenary.segHasFlexJoint,
      );

      cat.push(line);
      flexJointDistances.push(newDistances);
      catenaryLengths.push(this.filterList(catenary.segLength, withoutFlexJoint));
      hasFloater.push(this.filterList(catenary.segHasFloater, withoutFlexJoint));
      hasStiffener.push(this.filterList(catenary.segHasStiffener, withoutFlexJoint));
      hasStrake.push(this.filterList(catenary.segHasStrake, withoutFlexJoint));
      hasStressJoint.push(this.filterList(catenary.segHasStressJoint, withoutFlexJoint));
    });
    return {
      cat,
      allX,
      allY,
      catenaryLengths,
      hasFloater,
      hasStiffener,
      hasStrake,
      hasStressJoint,
      flexJointDistances,
    };
  }

  computeFullCatenaryLength(catenary): number {
    if (!catenary.length) {
      return 0;
    }
    let prevPoint = catenary[0];
    let length = 0;
    for (const point of catenary) {
      const newDist = this.pointDist(point, prevPoint);
      length += newDist;
      prevPoint = point;
    }
    return length;
  }

  makeSliceOrganizer(
    catenary: Array<{ x: number; y: number }>,
    segLengths: number[],
    flexJointDistances,
  ): {
    sliceOrganizer: number[];
    regionSliceOrganizer: number[][];
    flexJointPoints: number[];
  } {
    const catenaryLength = this.computeFullCatenaryLength(catenary);
    const presumedLength = segLengths.reduce((a, b) => +a + +b);
    const conversionFactor = catenaryLength / presumedLength;
    const sliceOrganizer: number[] = [0];
    const regionSliceOrganizer: number[][] = [];
    const maxPos = catenary.length;
    const flexJointPoints = [];

    let position = 0;
    let fullLength = 0;
    let flexJointDistanceIdx = 0;
    let segLengthIdx = 0;

    let length = 0;

    segLengths = segLengths.map((segLength) => +segLength * conversionFactor);
    flexJointDistances = flexJointDistances.map((fjDistance) => +fjDistance * conversionFactor);

    while (fullLength <= catenaryLength && position < maxPos - 2) {
      const newDist = this.pointDist(catenary[position], catenary[position + 1]);

      length += newDist;
      fullLength += newDist;

      if (fullLength >= flexJointDistances[flexJointDistanceIdx]) {
        flexJointPoints.push(catenary[position]);
        flexJointDistanceIdx++;
      }

      this.lineRegions?.forEach(({ beginning, end }, index) => {
        const convertedBeginning = catenaryLength - end * conversionFactor;
        const convertedEnd = catenaryLength - beginning * conversionFactor;

        if (fullLength >= convertedBeginning && (fullLength <= convertedEnd || beginning === 0)) {
          if (regionSliceOrganizer[index]) {
            regionSliceOrganizer[index].push(position);
            if (position === maxPos - 3) {
              regionSliceOrganizer[index].push(position + 1, position + 2, position + 3);
            }
          } else {
            regionSliceOrganizer[index] = [position];
          }
        }
      });

      if (length >= segLengths[segLengthIdx]) {
        sliceOrganizer.push(position);
        length = length - +segLengths[segLengthIdx];
        segLengthIdx++;
      }

      position++;
    }

    if (flexJointPoints.length < flexJointDistances.length) {
      flexJointPoints.push(catenary[catenary.length - 1]);
    }
    sliceOrganizer.push(catenary.length - 1);
    return { sliceOrganizer, flexJointPoints, regionSliceOrganizer };
  }

  getTypeById(id: number): string {
    return this.mapsService.getTypeById(id);
  }

  checkSegmentInternalAccessories(
    line,
    segId: number,
  ): { containsFloater: boolean; containsStrake: boolean } {
    let containsFloater = false;
    let containsStrake = false;
    if (line?.segConfig) {
      const segConfig = line.segConfig[segId];
      containsFloater = !!segConfig?.floaterConfig.length;
      containsStrake = !!segConfig?.strakeConfig.length;
    }
    return { containsFloater, containsStrake };
  }

  checkSegmentBeginAndEndAccessories(line: Line, segId: number): { containsStiffener: boolean } {
    const containsStiffenerBegin = line?.segConfig?.length
      ? line?.segConfig[segId].stiffenerBeginId !== -1
      : false;
    const containsStiffenerEnd = line?.segConfig?.length
      ? line?.segConfig[segId].stiffenerEndId !== -1
      : false;
    return {
      containsStiffener: containsStiffenerBegin || containsStiffenerEnd,
    };
  }

  isSelectedLine(lineId: number): boolean {
    return lineId === this.selectedLineId;
  }

  getLine(): Line {
    const simulation: Simulation = this.simulationManager.getSimulation();
    if (this.isAnflex) {
      return simulation.line.find((currLine) => this.isSelectedLine(currLine.internalId));
    }
    const line = simulation.line[0];
    this.selectedLineTitle = line.title;
    return line;
  }

  fetchWebSimulationCatenaryParams(catenariesLength: number): any {
    const catenaryLengths: number[] = [];
    const hasFloater: boolean[] = [];
    const hasStrake: boolean[] = [];
    const hasStiffener: boolean[] = [];
    const hasStressJoint: boolean[] = [];
    const line = this.getLine();

    if (!line) {
      return null;
    }

    line.segmentMesh.forEach((mesh, i) => {
      catenaryLengths.push(mesh.l);
      const materialType = this.getTypeById(line.segmentMaterialId[i]);
      const { containsFloater, containsStrake } = this.checkSegmentInternalAccessories(line, i);
      const { containsStiffener } = this.checkSegmentBeginAndEndAccessories(line, i);
      hasFloater.push(containsFloater);
      hasStrake.push(containsStrake);
      hasStiffener.push(containsStiffener);
      hasStressJoint.push(materialType === CONSTANTS.MATERIAL_TYPES.STRESS_JOINT);
    });

    const finalCatenaryLengths: number[][] = [];
    const finalHasFloater: boolean[][] = [];
    const finalHasStrake: boolean[][] = [];
    const finalHasStiffener: boolean[][] = [];
    const finalHasStressJoint: boolean[][] = [];

    for (let i = 0; i < catenariesLength; i++) {
      finalCatenaryLengths.push(catenaryLengths);
      finalHasFloater.push(hasFloater);
      finalHasStrake.push(hasStrake);
      finalHasStiffener.push(hasStiffener);
      finalHasStressJoint.push(hasStressJoint);
    }

    return {
      catenaryLengths: finalCatenaryLengths,
      hasFloater: finalHasFloater,
      hasStrake: finalHasStrake,
      hasStiffener: finalHasStiffener,
      hasStressJoint: finalHasStressJoint,
    };
  }

  addLineToLineGroup(
    lineGroup,
    lineClass,
    chartLineFunc,
    lineThickness,
    data,
    overlayId,
    tooltipId,
    first = true,
  ) {
    let path = null;
    if (first) {
      path = lineGroup
        .insert(CONSTANTS.PATH, ':first-child')
        .attr(CONSTANTS.STROKE, 'white')
        .attr(CONSTANTS.STROKE_WIDTH, lineThickness);
    } else {
      path = lineGroup
        .insert(CONSTANTS.PATH)
        .attr(CONSTANTS.STROKE, 'white')
        .attr(CONSTANTS.STROKE_WIDTH, lineThickness);
    }

    this.addLineUserEvents(path, overlayId, tooltipId);

    path
      .datum(data)
      .attr('class', lineClass)
      .attr(CONSTANTS.FILL, CONSTANTS.NONE_LOWER)
      .attr('d', (d) => chartLineFunc(d.points));
  }

  addLineUserEvents(path, overlayId, tooltipId): void {
    path
      .on(CONSTANTS.MOUSEOVER, (_, d) => {
        d3.select('#' + overlayId).style(CONSTANTS.VISIBILITY, CONSTANTS.VISIBLE);
        d3.select('#' + tooltipId).style(CONSTANTS.VISIBILITY, CONSTANTS.VISIBLE);
      })
      .on(CONSTANTS.MOUSEOUT, (_, d) => {
        d3.select('#' + overlayId).style(CONSTANTS.VISIBILITY, CONSTANTS.HIDDEN);
        d3.select('#' + tooltipId).style(CONSTANTS.VISIBILITY, CONSTANTS.HIDDEN);
      });
  }

  computeTotalLength(paths: d3.Selection<d3.BaseType, unknown, d3.BaseType, unknown>): number {
    return paths.nodes().reduce((acc: number, node: any) => node.getTotalLength() + acc, 0);
  }

  drawCatenaryByFiniteElements(): void {
    if (!this.hasDefinedMeshHeatmap) {
      return;
    }

    const lineGroup = d3.selectAll(`.${CONSTANTS.CATENARY_GROUP}`);
    const paths = lineGroup.selectAll(`.${CONSTANTS.CATENARY_LINE}`).remove();

    lineGroup.selectAll(`.${CONSTANTS.CATENARY_HEATMAP}`).remove();
    this.meshHeatMapData.totalPathLength = this.computeTotalLength(paths);

    let finiteElementsGroup;
    if (this.lineRegions?.length) {
      finiteElementsGroup = lineGroup
        .insert('g', '.overlay:last-of-type + *')
        .attr('class', 'finite-elements');
    }

    paths.nodes().forEach((node: SVGPathElement, index) => {
      const { overlayId, tooltipId } = this.meshHeatMapData.linesData[index];
      const samples = this.heatmap.samples(node, this.meshHeatMapData, index);
      const quads = this.heatmap.quads(samples);
      let path: any;

      if (this.lineRegions?.length) {
        path = this.drawWithRegions(finiteElementsGroup, quads);
      } else {
        path = this.drawWithHeatmap(lineGroup, quads);
      }

      this.addLineUserEvents(path, overlayId, tooltipId);
    });
  }

  drawWithHeatmap(
    lineGroup: d3.Selection<d3.BaseType, unknown, HTMLElement, any>,
    quads: any,
  ): any {
    return lineGroup
      .selectAll(`.${CONSTANTS.CATENARY_LINE}`)
      .data(quads)
      .enter()
      .append('path')
      .style(CONSTANTS.FILL, (d: { t: number }) => this.interpolateColor(d.t))
      .style(CONSTANTS.STROKE, (d: { t: number }) => this.interpolateColor(d.t))
      .attr('d', (d) => {
        const lineJoin = this.heatmap.joinLine(d[0], d[1], d[2], d[3], 5);
        if (lineJoin.includes('NaN')) {
          return '';
        }
        return lineJoin;
      })
      .attr('class', CONSTANTS.CATENARY_HEATMAP);
  }

  drawWithRegions(
    lineGroup: d3.Selection<d3.BaseType, unknown, HTMLElement, any>,
    quads: any,
  ): any {
    return lineGroup
      .selectAll(`.${CONSTANTS.CATENARY_LINE}`)
      .data(quads)
      .enter()
      .insert(CONSTANTS.PATH)
      .style(CONSTANTS.STROKE, 'white')
      .style(CONSTANTS.FILL, 'white')
      .attr('d', (d) => {
        const lineJoin = this.heatmap.joinLine(d[0], d[1], d[2], d[3], 5);
        if (lineJoin.includes('NaN')) {
          return '';
        }
        return lineJoin;
      })
      .attr('class', CONSTANTS.CATENARY_HEATMAP);
  }

  clearMeshHeatmap(): void {
    const lineGroup = d3.selectAll(`.${CONSTANTS.CATENARY_GROUP}`);
    lineGroup.selectAll(`.${CONSTANTS.CATENARY_HEATMAP}`).remove();
    const line = lineGroup.selectAll(`.${CONSTANTS.CATENARY_LINE}`);
    line.attr(CONSTANTS.STROKE, 'white').attr(CONSTANTS.STROKE_WIDTH, this.lineThickness);
    this.clearMeshHeatmapData();
  }

  clearRegions(): void {
    d3.selectAll(`.regions`).remove();
  }

  clearMeshHeatmapData(): void {
    this.meshHeatMapData.totalPathLength = 0;
    this.meshHeatMapData.minLength = 0;
    this.meshHeatMapData.maxLength = 0;
    this.meshHeatMapData.elementsLength = [];
    this.meshHeatMapData.linesData = [];
    this.meshHeatMapData.segmentLengths = [];
  }

  storeLineData(overlayId: string, tooltipId: string): void {
    if (!this.hasDefinedMeshHeatmap) {
      return;
    }
    this.meshHeatMapData.linesData.push({ overlayId, tooltipId });
  }

  drawCatenaries(catenaryInfo: CatenaryInfo): void {
    this.clearMeshHeatmap();
    this.clearRegions();

    const {
      cat,
      catenaryLengths: desktopCatenaryLengths,
      hasFloater: desktopHasFloater,
      hasStiffener: desktopHasStiffener,
      hasStrake: desktopHasStrake,
      hasStressJoint: desktopHasStressJoint,
      flexJointDistances,
    } = catenaryInfo;

    const catenaryWebParams = this.fetchWebSimulationCatenaryParams(cat.length);
    if (!catenaryWebParams) {
      return;
    }

    const { catenaryLengths, hasFloater, hasStrake, hasStiffener, hasStressJoint } =
      catenaryWebParams;

    const d3LineFunction = d3
      .line()
      .x((d: any) => this.fullXScale(d[0]))
      .y((d: any) => this.yScale(d[1]));

    const chartLine = (d) => {
      const dVect = d.map((o) => [o.x, o.y]);
      return d3LineFunction(dVect);
    };

    cat.forEach((catenary, i) => {
      let segLengths: number[] = catenaryLengths[i];
      let segHasFloat: boolean[] = hasFloater[i];
      let segHasStrake: boolean[] = hasStrake[i];
      let segHasStiffener: boolean[] = hasStiffener[i];
      let segHasStressJoint: boolean[] = hasStressJoint[i];

      if (this.shouldDrawDesktopSegments) {
        segLengths = desktopCatenaryLengths[i];
        segHasFloat = desktopHasFloater[i];
        segHasStiffener = desktopHasStiffener[i];
        segHasStrake = desktopHasStrake[i];
        segHasStressJoint = desktopHasStressJoint[i];
      }

      const { sliceOrganizer, flexJointPoints, regionSliceOrganizer } = this.makeSliceOrganizer(
        catenary,
        segLengths,
        flexJointDistances[i],
      );
      const segLength = sliceOrganizer.length - 1;
      const lineGroup = this.chart.insert('g').attr('class', CONSTANTS.CATENARY_GROUP);
      const divisions = this.chart.insert('g').attr('class', CONSTANTS.DIVISIONS_GROUP);
      this.parseCatenaryToLengths(catenary, sliceOrganizer);

      sliceOrganizer.forEach((slice, index) => {
        const lastSegment = index === segLength;
        const divisionPoint = slice;
        this.drawLineDivisions(divisions, divisionPoint, catenary);

        if (!lastSegment) {
          const lenCat = catenary.length;
          const segTitle = `Seg${segLength - index}`;
          const points = catenary.slice(slice, Math.min(sliceOrganizer[index + 1] + 1, lenCat));
          const data = { segTitle, points };
          const overlayClass = 'overlay';
          const overlayId: string = 'Cat' + i + '-' + data.segTitle + '-overlay';
          const tooltipId: string = 'Tooltip-Cat' + i + '-' + data.segTitle + '-overlay';
          let wrapperThickness = this.lineThickness;

          lineGroup
            .insert(CONSTANTS.PATH, ':first-child')
            .datum(data)
            .attr('class', overlayClass)
            .attr(CONSTANTS.ID, overlayId)
            .attr(CONSTANTS.STROKE_WIDTH, wrapperThickness + 10)
            .style(CONSTANTS.VISIBILITY, CONSTANTS.HIDDEN)
            .attr(CONSTANTS.FILL, CONSTANTS.NONE_LOWER)
            .on(CONSTANTS.MOUSEOVER, this.showTooltipAndOverlay(overlayId, tooltipId))
            .on(CONSTANTS.MOUSEOUT, this.hideTooltipAndOverlay(overlayId, tooltipId))
            .attr('d', (d) => chartLine(d.points));

          this.addLineToLineGroup(
            lineGroup,
            'catenary-line',
            chartLine,
            this.lineThickness,
            data,
            overlayId,
            tooltipId,
            false,
          );
          this.storeLineData(overlayId, tooltipId);
          this.drawAccessories(
            lineGroup,
            index,
            chartLine,
            data,
            overlayId,
            tooltipId,
            catenary,
            segHasStiffener,
            segHasStrake,
            segHasFloat,
            segHasStressJoint,
            flexJointPoints,
          );

          const tooltipDataPreset = {
            catenary,
            catenaryIndex: i,
            segIndex: index,
            segLength,
            sliceOrganizer,
          };
          this.drawTooltip(tooltipDataPreset);
        }
      });

      this.overlayRegions(catenary, lineGroup, regionSliceOrganizer, catenary.length);
    });

    this.drawCatenaryByFiniteElements();
  }

  drawAccessories(
    lineGroup,
    index,
    chartLine,
    data,
    overlayId,
    tooltipId,
    catenary,
    segHasStiffener,
    segHasStrake,
    segHasFloat,
    segHasStressJoint,
    flexJointPoints,
  ): void {
    const accessoryGroup = lineGroup
      .insert('g')
      .attr('class', `${CONSTANTS.ACCESSORY_GROUP} seg-${index}`);

    let wrapperThickness = this.lineThickness;
    if (segHasStiffener[index] && this.showStiffeners) {
      wrapperThickness = this.lineRegions?.length ? wrapperThickness : wrapperThickness + 8;
      this.addLineToLineGroup(
        accessoryGroup,
        'stiffener-line',
        chartLine,
        wrapperThickness,
        data,
        overlayId,
        tooltipId,
        !this.lineRegions?.length,
      );
    }

    if (segHasStrake[index] && this.showStrakes) {
      wrapperThickness = this.lineRegions?.length ? wrapperThickness : wrapperThickness + 8;
      this.addLineToLineGroup(
        accessoryGroup,
        'strake-line',
        chartLine,
        wrapperThickness,
        data,
        overlayId,
        tooltipId,
        !this.lineRegions?.length,
      );
    }

    if (segHasFloat[index] && this.showFloaters) {
      wrapperThickness = this.lineRegions?.length ? wrapperThickness : wrapperThickness + 8;
      this.addLineToLineGroup(
        accessoryGroup,
        'floater-line',
        chartLine,
        wrapperThickness,
        data,
        overlayId,
        tooltipId,
        !this.lineRegions?.length,
      );
    }

    if (segHasStressJoint[index] && this.showStressJoint) {
      wrapperThickness += 8;
      this.addLineToLineGroup(
        accessoryGroup,
        'stressjoint-line',
        chartLine,
        wrapperThickness,
        data,
        overlayId,
        tooltipId,
        !this.lineRegions?.length,
      );
    }

    if (this.showFlexJoint) {
      wrapperThickness = this.lineRegions?.length ? wrapperThickness : this.flexjointStroke;
      for (const flexJointPoint of flexJointPoints) {
        this.addZeroLengthSegmentToLineGroup(
          accessoryGroup,
          'flexjoint-line',
          chartLine,
          wrapperThickness,
          flexJointPoint,
          catenary,
        );
      }
    }
  }

  overlayRegions(catenary, lineGroup, regionSliceOrganizer, lenCat): void {
    if (!this.lineRegions?.length) {
      return;
    }

    const regionsGroup = lineGroup.append('g', ':first-child').attr('class', 'regions');
    const d3LineFunction = d3
      .line()
      .x((d: any) => this.fullXScale(d[0]))
      .y((d: any) => this.yScale(d[1]));
    const chartLine = (d) => {
      const dVect = d.map((o) => [o.x, o.y]);
      return d3LineFunction(dVect);
    };

    this.lineRegions.forEach((region, i) => {
      const points = catenary.slice(
        regionSliceOrganizer[i]?.[0],
        Math.min(regionSliceOrganizer[i]?.[regionSliceOrganizer[i]?.length - 1], lenCat),
      );

      const data = { label: `${region.label} form ${region.beginning} to ${region.end}`, points };
      const overlayId: string = `Cat${i}-${region.label}-overlay`;
      const tooltipId: string = `Tooltip-Cat${i}-${region.label}-overlay`;
      let path = null;

      path = regionsGroup
        .insert(CONSTANTS.PATH)
        .attr(CONSTANTS.STROKE, this.regionColor)
        .attr(CONSTANTS.STROKE_WIDTH, this.lineThickness + 10);

      const tooltipDataPreset = {
        catenary,
        catenaryIndex: i,
        segIndex: i,
        segLength: region.beginning + region.end,
        regionSliceOrganizer,
      };
      this.drawTooltip(tooltipDataPreset, true);
      this.addLineUserEvents(path, overlayId, tooltipId);
      path
        .datum(data)
        .attr('class', 'region')
        .attr(CONSTANTS.FILL, CONSTANTS.NONE_LOWER)
        .attr('d', (d) => chartLine(d.points));

      lineGroup
        .insert(CONSTANTS.PATH, ':first-child')
        .datum(data)
        .attr('class', 'overlay')
        .attr(CONSTANTS.ID, overlayId)
        .attr(CONSTANTS.STROKE_WIDTH, this.lineThickness + 20)
        .style(CONSTANTS.VISIBILITY, CONSTANTS.HIDDEN)
        .attr(CONSTANTS.FILL, CONSTANTS.NONE_LOWER)
        .on(CONSTANTS.MOUSEOVER, this.showTooltipAndOverlay(overlayId, tooltipId))
        .on(CONSTANTS.MOUSEOUT, this.hideTooltipAndOverlay(overlayId, tooltipId))
        .attr('d', (d) => chartLine(d.points));
    });
  }

  drawLineDivisions(divisions, point, catenary): void {
    divisions
      .insert('circle')
      .attr('class', 'line-division')
      .attr('cx', this.fullXScale(catenary[point].x))
      .attr('cy', this.yScale(catenary[point].y))
      .attr('r', this.divisionCircleRadius);
  }

  buildTooltipData(tooltipData: TooltipData): {
    title: string;
    points: { x: number; y: number }[];
  } {
    const { catenary, segIndex, segLength, sliceOrganizer } = tooltipData;
    const title = 'Seg' + (segLength - segIndex);
    const points = catenary.slice(sliceOrganizer[segIndex], sliceOrganizer[segIndex + 1]);
    return { title, points };
  }

  buildTooltipForRegion(tooltipData: TooltipData): {
    title: string;
    points: { x: number; y: number }[];
  } {
    const { catenary, segIndex, regionSliceOrganizer } = tooltipData;
    const reverseRegionSlices = regionSliceOrganizer;
    const title = this.lineRegions[segIndex].label;
    const points = catenary.slice(
      reverseRegionSlices[segIndex]?.[0],
      reverseRegionSlices[segIndex]?.[reverseRegionSlices[segIndex].length - 1],
    );
    return { title, points };
  }

  drawTooltip(tooltipData: TooltipData, forRegion = false): void {
    const { catenaryIndex } = tooltipData;

    let tooltipBuilt;
    if (forRegion) {
      tooltipBuilt = this.buildTooltipForRegion(tooltipData);
    } else {
      tooltipBuilt = this.buildTooltipData(tooltipData);
    }

    const { points, title } = tooltipBuilt;
    if (!points.length) {
      return;
    }

    const id = `Tooltip-Cat${catenaryIndex}-${title}-overlay`;
    const { svgX, svgY } = points.reduce(
      (itemA, itemB, index) => {
        if (index === points.length - 1) {
          return {
            svgX: (itemA.svgX + itemB.x) / points.length,
            svgY: (itemA.svgY + itemB.y) / points.length,
          };
        }
        return { svgX: itemA.svgX + itemB.x, svgY: itemA.svgY + itemB.y };
      },
      { svgX: 0, svgY: 0 },
    );

    const sizeYBrick = this.height / this.tooltipScallingDenominator;
    const triangleArea = Math.pow(this.triangleSideSize, 2);
    const bottomRegionOffset = (svgX + 10) * 1.1;

    const group = this.chart
      .insert('g')
      .attr('id', id)
      .style(CONSTANTS.VISIBILITY, CONSTANTS.HIDDEN);

    group
      .on(CONSTANTS.MOUSEOVER, (_) => {
        d3.select(id).style(CONSTANTS.VISIBILITY, CONSTANTS.VISIBLE);
      })
      .on(CONSTANTS.MOUSEOUT, (_) => {
        d3.select(id).style(CONSTANTS.VISIBILITY, CONSTANTS.HIDDEN);
      })
      .attr(
        CONSTANTS.TRANSFORM,
        `translate(${
          this.fullXScale(forRegion ? bottomRegionOffset : svgX) + this.triangleSideSize
        }, ${this.yScale(svgY)})`,
      );

    group
      .insert(CONSTANTS.PATH)
      .attr('class', 'tooltip')
      .attr('d', d3.symbol().type(d3.symbolDiamond).size(triangleArea))
      .attr(CONSTANTS.TRANSFORM, 'translate(0,0)');
    const tooltipText = group
      .insert('text')
      .attr('class', 'tooltip-text')
      .attr('x', this.triangleSideSize)
      .attr('y', 0)
      .style(CONSTANTS.FONT_SIZE, this.tooltipFontSize + 'px')
      .style('alignment-baseline', 'middle')
      .text(title);

    const background = group
      .insert('rect', ':first-child')
      .attr('class', 'tooltip tooltip-box')
      .attr('x', 0)
      .attr('y', -sizeYBrick / 2)
      .attr(CONSTANTS.WIDTH, 0)
      .attr(CONSTANTS.HEIGHT, sizeYBrick);

    setTimeout(() => {
      if (title.length > 6) {
        background.attr(CONSTANTS.WIDTH, tooltipText.node().clientWidth + 60);
      } else {
        background.attr(CONSTANTS.WIDTH, tooltipText.node().clientWidth + 30);
      }
    });
  }

  pointDist(p1: { x: number; y: number }, p2: { x: number; y: number }): number {
    return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
  }

  onlyDistinctPoints(points) {
    const pointMap = {};
    for (const point of points) {
      const key = point.x.toString();
      if (!pointMap.hasOwnProperty(key)) {
        pointMap[key] = point;
      }
    }
    const distinctPoints = [];
    for (const key of Object.keys(pointMap)) {
      distinctPoints.push(pointMap[key]);
    }
    return distinctPoints;
  }

  addZeroLengthSegmentToLineGroup(
    lineGroup,
    lineClass,
    chartLineFunc,
    lineThickness,
    position,
    allPoints,
  ) {
    const distinctPoints = this.onlyDistinctPoints(allPoints);
    if (distinctPoints.length < 2) {
      return;
    }
    const sortedPointsByDist = [...distinctPoints].sort(
      (p1, p2) => this.pointDist(p1, position) - this.pointDist(p2, position),
    );
    const derivativePoints = sortedPointsByDist.slice(0, 2).sort((p1, p2) => p1.x - p2.x);
    lineGroup
      .insert(CONSTANTS.PATH)
      .datum({ points: derivativePoints })
      .attr(CONSTANTS.STROKE_WIDTH, lineThickness)
      .attr('class', lineClass)
      .attr(CONSTANTS.FILL, CONSTANTS.NONE_LOWER)
      .attr('d', (d) => chartLineFunc(d.points));
  }

  parseCatenaryToLengths(catenary: { x: number; y: number }[], sliceOrganizer): void {
    if (!this.hasDefinedMeshHeatmap) {
      return;
    }

    this.meshHeatMapData.segmentLengths = sliceOrganizer;
    const newElementsLengthArray = catenary.map((point, index) => {
      if (index < catenary.length - 1) {
        const p1 = point;
        const p2 = catenary[index + 1];
        const L = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
        if (!this.meshHeatMapData.minLength || this.meshHeatMapData.minLength > L) {
          this.meshHeatMapData.minLength = L;
        }
        if (!this.meshHeatMapData.maxLength || this.meshHeatMapData.maxLength < L) {
          this.meshHeatMapData.maxLength = L;
        }
        return L;
      }
      return null;
    });

    this.meshHeatMapData.elementsLength.push(...newElementsLengthArray);
  }

  showTooltipAndOverlay(overlayId, tooltipId): void {
    d3.select('#' + overlayId).style(CONSTANTS.VISIBILITY, CONSTANTS.VISIBLE);
    d3.select('#' + tooltipId).style(CONSTANTS.VISIBILITY, CONSTANTS.VISIBLE);
  }

  hideTooltipAndOverlay(overlayId, tooltipId): void {
    d3.select('#' + overlayId).style(CONSTANTS.VISIBILITY, CONSTANTS.HIDDEN);
    d3.select('#' + tooltipId).style(CONSTANTS.VISIBILITY, CONSTANTS.HIDDEN);
  }

  generateGraphic() {
    const catenaryInfo: CatenaryInfo = this.computeCatenaries();

    if (!catenaryInfo.cat.length) {
      this.allCatenariesNoConverged = true;
      return;
    }

    if (this.failedCatenaries.length) {
      this.catenaryNoConverged = true;
    }

    if (!this.setBounds(catenaryInfo)) {
      return;
    }

    this.setAxisAndGrids();
    this.drawCatenaries(catenaryInfo);
  }

  checkCatenaryRequirements(): boolean {
    this.simulationProblem = false;
    this.setErrorsBySimulationMaterials();
    this.setErrorsBySimulationLines();
    return !this.simulationProblem;
  }

  setErrorsBySimulationMaterials(): void {
    const hasMaterial = this.simulationManager.hasAnyMaterial();
    if (!hasMaterial) {
      this.simulationProblem = true;
      this.noMaterial = true;
      return;
    }
    this.noMaterial = false;
  }

  setErrorsBySimulationLines(): void {
    const simulation = this.simulationManager.getSimulation();
    if (!simulation.line?.length) {
      this.simulationProblem = true;
      this.noLine = true;
      return;
    }
    this.noLine = false;
    this.setErrorsByLineProjection(simulation);
  }

  setErrorsByLineProjection(simulation: Simulation): void {
    simulation.line.forEach((line) => {
      if (line.projection <= 0 && line.calcType === CALC_TYPES.PROJECTION) {
        this.simulationProblem = true;
        this.zeroProjection = true;
        return;
      }
      if (line.angle <= 0 && line.calcType === CALC_TYPES.ANGLE) {
        this.simulationProblem = true;
        this.zeroProjection = true;
        return;
      }
      this.zeroProjection = false;
    });
  }

  toggleChart(chart: string): void {
    this[chart] = !this[chart];
    this.generateGraphic();
  }

  toggleDesktopView(): void {
    this.useDesktopRepresentation = !this.useDesktopRepresentation;
    this.generateGraphic();
  }

  get isAnflex(): boolean {
    return this.environmentPropsService.isAnflex;
  }

  get shouldDrawDesktopSegments(): boolean {
    return this.useDesktopRepresentation && !this.hasDefinedMeshHeatmap;
  }
}
